"use client";

import { useTransition, useState, type ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import Image, { type ImageProps } from "next/image";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InputTextField,
  NutcakeSquirrel,
  Typography,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@drpcrd/shared/ui";
import { submitOfferPageForm } from "./offer-page-actions";
import type { OfferSlug } from "./offers";

const COMPANY_TYPE_OPTIONS = [
  { value: "Brand", label: "Brand" },
  { value: "Agency", label: "Agency" },
  { value: "Creator", label: "Creator" },
  { value: "Talent Agent", label: "Talent Agent / Agency" },
  { value: "Legal Professional", label: "Legal Professional" },
  { value: "Other", label: "Other" },
] as const;

const schema = z.object({
  botField: z.string().optional(),
  firstName: z.string().trim().min(1, { message: "First name is required" }),
  lastName: z.string().trim().min(1, { message: "Last name is required" }),
  company: z.string().trim().min(1, { message: "Company is required" }),
  companyType: z.enum(
    [
      "Brand",
      "Agency",
      "Creator",
      "Talent Agent",
      "Legal Professional",
      "Other",
    ],
    {
      error: "Please select a type of company",
    },
  ),
  email: z.email({ message: "Please enter a valid work email address" }),
  newsletterOptIn: z.boolean().optional(),
});

type OfferPageFormValues = z.infer<typeof schema>;

type Props = {
  /** Route slug of the offer page, so the lead is labelled per offer. */
  offer: OfferSlug;
  /** Card heading; pass a fragment to style part of it. */
  heading: ReactNode;
  /** Round avatar shown before the heading; defaults to the Nutcake squirrel. */
  avatarSrc?: ImageProps["src"];
  /** Alt text for the avatar. */
  avatarAlt?: string;
  /** Submit button label. */
  submitLabel?: string;
  /** Copy shown in place of the form once the lead has been sent. */
  successMessage?: string;
};

/**
 * Shared signup form for the offer landing pages under `/offer/`. On
 * success, the form is replaced with a confirmation message.
 */
export const OfferPageForm = ({
  offer,
  heading,
  avatarSrc,
  avatarAlt = "",
  submitLabel = "Get Nutcake today!",
  successMessage = "Thanks for requesting access — we’ll be in touch shortly to get you set up with free access to Nutcake.",
}: Props) => {
  const [isPending, startTransition] = useTransition();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const searchParams = useSearchParams();

  const form = useForm<OfferPageFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      botField: "",
      firstName: "",
      lastName: "",
      company: "",
      companyType: undefined,
      email: "",
      newsletterOptIn: false,
    },
    mode: "onSubmit",
  });

  const onSubmit = (values: OfferPageFormValues) => {
    setErrorMessage(null);
    startTransition(async () => {
      const result = await submitOfferPageForm({
        ...values,
        offer,
        landingPage: window.location.origin + window.location.pathname,
        utmSource: searchParams.get("utm_source") ?? undefined,
        utmMedium: searchParams.get("utm_medium") ?? undefined,
        utmCampaign: searchParams.get("utm_campaign") ?? undefined,
        utmTerm: searchParams.get("utm_term") ?? undefined,
        utmContent: searchParams.get("utm_content") ?? undefined,
      });
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(
          result.data ??
            "Something went wrong. Please try again or email us directly.",
        );
      }
    });
  };

  if (isSubmitted) {
    return (
      <div className="border-primary-600 flex w-full flex-col gap-3 rounded-3xl border-4 px-3 py-4 shadow-[0px_1px_8px_0px_rgba(0,0,0,0.08)]">
        <Typography
          weight="semibold"
          className="font-poppins text-primary-700 text-xl!"
        >
          You&apos;re in!
        </Typography>
        <Typography size="lg" className="leading-3">
          {successMessage}
        </Typography>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        className="border-primary-600 flex w-full flex-col gap-3 rounded-3xl border-4 px-3 py-4 shadow-[0px_1px_8px_0px_rgba(0,0,0,0.08)]"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
      >
        <div className="flex items-center gap-1.5">
          {avatarSrc ? (
            <Image
              src={avatarSrc}
              alt={avatarAlt}
              className="size-4 shrink-0 rounded-full object-cover"
            />
          ) : (
            <NutcakeSquirrel fill="#5e32c7" className="h-4 w-4 shrink-0" />
          )}
          <Typography
            weight="semibold"
            className="font-poppins text-primary-700 mt-0.5 text-lg!"
          >
            {heading}
          </Typography>
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-pink-500 bg-pink-50 px-4 py-3">
            <Typography size="sm" color="black">
              {errorMessage}
            </Typography>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <InputTextField
            control={form.control}
            name="firstName"
            label="First name"
            placeholder="First name"
            autoComplete="given-name"
            maxLength={50}
            required
          />
          <InputTextField
            control={form.control}
            name="lastName"
            label="Last name"
            placeholder="Last name"
            autoComplete="family-name"
            maxLength={50}
            required
          />
        </div>

        <InputTextField
          control={form.control}
          name="company"
          label="Company"
          placeholder="Your company name"
          autoComplete="organization"
          maxLength={100}
          required
        />

        <FormField
          control={form.control}
          name="companyType"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="flex w-full flex-row gap-1 font-sans text-sm font-semibold">
                Type of company
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {COMPANY_TYPE_OPTIONS.map(({ value, label }) => (
                    <SelectItem className="text-lg" key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <InputTextField
          control={form.control}
          name="email"
          label="Work email"
          type="email"
          placeholder="Your email address"
          autoComplete="email"
          required
        />

        <FormField
          control={form.control}
          name="newsletterOptIn"
          render={({ field }) => (
            <FormItem className="flex cursor-pointer flex-row items-center gap-1.5 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="border-primary-700 data-[state=checked]:bg-primary-700 size-2.5 cursor-pointer bg-white data-[state=checked]:text-white [&_svg]:size-2"
                />
              </FormControl>
              <FormLabel className="cursor-pointer font-sans text-sm font-semibold text-black">
                Get Nutcake&rsquo;s news (once a month)
              </FormLabel>
            </FormItem>
          )}
        />

        {/* Honeypot: hidden from real users, bots fill it and get silently
            rejected. readOnly is what keeps browser autofill and password
            managers out (they skip read-only fields, but DOM-scripting bots
            set the value anyway); the data attributes cover 1Password and
            LastPass explicitly. */}
        <input
          {...form.register("botField")}
          type="text"
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          className="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
        />

        <Button
          type="submit"
          variant="filled"
          size="lg"
          className="w-full justify-center"
          loading={isPending}
          disabled={isPending}
        >
          {isPending ? "Sending…" : submitLabel}
        </Button>
      </form>
    </Form>
  );
};
